import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface ChildStats {
  totalActivities: number;
  mathCompleted: number;
  readingCompleted: number;
  scienceCompleted: number;
  totalPoints: number;
  totalTimeSpent: number;
  averageScore: number;
  completedActivityIds: string[];
}

export function useChildStats(childId: string | null) {
  const [stats, setStats] = useState<ChildStats>({
    totalActivities: 0,
    mathCompleted: 0,
    readingCompleted: 0,
    scienceCompleted: 0,
    totalPoints: 0,
    totalTimeSpent: 0,
    averageScore: 0,
    completedActivityIds: [],
  });
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    if (!childId) {
      setStats({
        totalActivities: 0,
        mathCompleted: 0,
        readingCompleted: 0,
        scienceCompleted: 0,
        totalPoints: 0,
        totalTimeSpent: 0,
        averageScore: 0,
        completedActivityIds: [],
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('child_progress')
        .select(`
          *,
          activities (
            subject
          )
        `)
        .eq('child_id', childId);

      if (error) throw error;

      const progress = data || [];
      const totalActivities = progress.length;
      const mathCompleted = progress.filter(p => p.activities?.subject === 'math').length;
      const readingCompleted = progress.filter(p => p.activities?.subject === 'reading').length;
      const scienceCompleted = progress.filter(p => p.activities?.subject === 'science').length;
      const totalPoints = progress.reduce((sum, p) => sum + (p.points_earned || 0), 0);
      const totalTimeSpent = progress.reduce((sum, p) => sum + (p.time_spent || 0), 0);
      const averageScore = totalActivities > 0 
        ? Math.round(progress.reduce((sum, p) => sum + p.score, 0) / totalActivities)
        : 0;
      const completedActivityIds = progress.map(p => p.activity_id);

      setStats({
        totalActivities,
        mathCompleted,
        readingCompleted,
        scienceCompleted,
        totalPoints,
        totalTimeSpent,
        averageScore,
        completedActivityIds,
      });
    } catch (error) {
      console.error('Error fetching child stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [childId]);

  return {
    stats,
    loading,
    refetch: fetchStats,
  };
}